import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import apiService from '../services/apiService';
import PROMIS33Questionnaire from './PROMIS33Questionnaire';
import HOOSQuestionnaire from './HOOSQuestionnaire';
import DEGSQuestionnaire from './DEGSQuestionnaire';
import InfectionQuestionnaire from './InfectionQuestionnaire';
import WaistMeasurement from './WaistMeasurement';
import WeightMeasurement from './WeightMeasurement';
import SurgeryTips from './SurgeryTips';
import KetoneTest from './KetoneTest';
import QoR15Questionnaire from './QoR15Questionnaire';
import BloodPressureMeasurement from './BloodPressureMeasurement';
import YouTubeFrame from './YouTubeFrame';

function DayDetail({ day, phaseId, patientData, language = 'en', onClose, onTaskUpdate, videoData }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [localDay, setLocalDay] = useState(day);

  const [showPROMIS33, setShowPROMIS33] = useState(false);
  const [currentPROMIS33TaskId, setCurrentPROMIS33TaskId] = useState('baseline-promis33');
  const [showHOOS, setShowHOOS] = useState(false);
  const [currentHOOSTaskId, setCurrentHOOSTaskId] = useState('baseline-hoos');
  const [showDEGS, setShowDEGS] = useState(false);
  const [currentDEGSTaskId, setCurrentDEGSTaskId] = useState('baseline-degs');
  const [showInfection, setShowInfection] = useState(false);
  const [currentInfectionTaskId, setCurrentInfectionTaskId] = useState('baseline-infection');
  const [showWaistMeasurement, setShowWaistMeasurement] = useState(false);
  const [showWeightMeasurement, setShowWeightMeasurement] = useState(false);
  const [currentWaistTaskId, setCurrentWaistTaskId] = useState('day1-waist');
  const [currentWeightTaskId, setCurrentWeightTaskId] = useState('day1-weight');
  const [showKetoneTest, setShowKetoneTest] = useState(false);
  const [currentKetoneTaskId, setCurrentKetoneTaskId] = useState('day1-ketostix');
  const [showQoR15, setShowQoR15] = useState(false);
  const [currentQoR15TaskId, setCurrentQoR15TaskId] = useState('day24-qor15');
  const [showBloodPressure, setShowBloodPressure] = useState(false);
  const [currentBloodPressureTaskId, setCurrentBloodPressureTaskId] = useState('');

  const [ohsScore, setOhsScore] = useState(null);
  const [ohsScoreDay24, setOhsScoreDay24] = useState(null);
  const [sf36Scores, setSf36Scores] = useState(null);
  const [sf36ScoresDay24, setSf36ScoresDay24] = useState(null);
  const [sf36Responses, setSf36Responses] = useState(null);
  const [sf36ResponsesDay24, setSf36ResponsesDay24] = useState(null);
  
  // États dynamiques pour tous les jours OHS et SF-36
  const [ohsScores, setOhsScores] = useState({});
  const [sf36ScoresAll, setSf36ScoresAll] = useState({});
  const [sf36ResponsesAll, setSf36ResponsesAll] = useState({});
  const [waistMeasurement, setWaistMeasurement] = useState(null);
  const [weightMeasurement, setWeightMeasurement] = useState(null);

  // Utiliser les traductions correctement
  const t = translations[language] || translations.en;

  // Fonction pour nettoyer les doublons dans les tâches
  const removeDuplicateTasks = (tasks) => {
    const seen = new Set();
    return tasks.filter(task => {
      if (seen.has(task.taskId)) {
        return false;
      }
      seen.add(task.taskId);
      return true;
    });
  };

  // Mettre à jour le cache de la timeline pour éviter un rechargement complet côté Timeline
  const updateTimelineCache = (serverResponse) => {
    try {
      if (!patientData || !patientData.patientId) return;
      if (!serverResponse || !serverResponse.success) return;
      
      // Nettoyer les doublons avant de mettre en cache
      const cleanedResponse = {
        ...serverResponse,
        timeline: serverResponse.timeline ? serverResponse.timeline.map(phase => ({
          ...phase,
          days: phase.days ? phase.days.map(day => ({
            ...day,
            tasks: removeDuplicateTasks(day.tasks)
          })) : phase.days
        })) : serverResponse.timeline
      };
      
      const cacheKey = `timeline_data_${patientData.patientId}`;
      const cacheData = { data: cleanedResponse, timestamp: Date.now() };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (e) {
      // Ignorer silencieusement
    }
  };

  // Recharger les données depuis le serveur quand le composant se monte
  useEffect(() => {
    const reloadDayData = async () => {
      if (!patientData || !patientData.patientId) return;
      
      try {
        const response = await apiService.getPatientTimeline(patientData.patientId);
        if (response.success && response.timeline) {
          updateTimelineCache(response);
          const phase = response.timeline.find(p => p.phaseId === phaseId);
          if (phase) {
            const updatedDay = phase.days.find(d => d.dayNumber === day.dayNumber);
            if (updatedDay) {
              // Nettoyer les doublons lors du chargement initial
              const cleanedDay = {
                ...updatedDay,
                tasks: removeDuplicateTasks(updatedDay.tasks)
              };
              setLocalDay(cleanedDay);
            }
          }
        }
      } catch (error) {
        console.error('Erreur rechargement données jour:', error);
      }
    };

    reloadDayData();
  }, [patientData, phaseId, day.dayNumber]);

  // Protection supplémentaire : nettoyer les doublons à chaque rendu
  useEffect(() => {
    if (localDay && localDay.tasks) {
      const cleanedTasks = removeDuplicateTasks(localDay.tasks);
      if (cleanedTasks.length !== localDay.tasks.length) {
        console.log('Nettoyage des doublons détecté, mise à jour de l\'état local');
        setLocalDay(prevDay => ({
          ...prevDay,
          tasks: cleanedTasks
        }));
      }
    }
  }, [localDay]);

  const getTaskIcon = (taskType, taskId) => {
    // Si c'est une tâche de tension artérielle, utiliser l'icône spécifique
    if (taskId && taskId.includes('blood-pressure')) {
      return '/images/pression-arterielle-icon.png';
    }
    
    // Si c'est une tâche de mesure de poids, utiliser l'icône spécifique
    if (taskId && taskId.includes('weight')) {
      return '/images/balance-corporelle-icon.png';
    }
    
    // Si c'est une tâche de test de corps cétoniques, utiliser l'icône spécifique
    if (taskId && taskId.includes('ketostix')) {
      return '/images/test-durine.png';
    }
    
    // Si c'est une tâche de mesure du tour de taille, utiliser l'icône spécifique
    if (taskId && taskId.includes('waist')) {
      return '/images/tour-taille-icon.png';
    }
    
    // Si c'est une tâche de test de selles, utiliser l'icône spécifique
    if (taskId && taskId.includes('stool')) {
      return '/images/test-de-selles-icon.png';
    }
    
    // Si c'est une tâche de prise de sang, utiliser l'icône spécifique
    if (taskId && taskId.includes('blood')) {
      return '/images/prise-de-sang-icon.png';
    }
    
    // Si c'est une tâche de questionnaire, utiliser l'icône spécifique
    if (taskId && (taskId.includes('ohs') || taskId.includes('sf36') || taskId.includes('qor15') || taskId.includes('promis33') || taskId.includes('hoos') || taskId.includes('infection') || taskId.includes('degs'))) {
      return '/images/questionnaire-icon.png';
    }
    
    switch (taskType) {
      case 'sample':
        return '🧪';
      case 'questionnaire':
        return '📋';
      case 'measurement':
        return '📏';
      case 'guidance':
        return '💡';
      case 'input':
        return '✏️';
      case 'test':
        return '🔬';
      case 'tips':
        return '💪';
      case 'support':
        return '🤝';
      default:
        return '📝';
    }
  };

  const getTaskTypeColor = (taskType) => {
    switch (taskType) {
      case 'sample':
        return '#ef4444';
      case 'questionnaire':
        return '#3b82f6';
      case 'measurement':
        return '#10b981';
      case 'guidance':
        return '#f59e0b';
      case 'input':
        return '#8b5cf6';
      case 'test':
        return '#06b6d4';
      case 'tips':
        return '#84cc16';
      case 'support':
        return '#ec4899';
      default:
        return '#6b7280';
    }
  };

  const handleTaskClick = (task) => {
    console.log('handleTaskClick appelé avec:', task);
    


    // Si c'est le questionnaire PROMIS-33 (baseline et autres jours avec promis33)
    if (task.taskId.includes('promis33')) {
      console.log('PROMIS-33 détecté, taskId:', task.taskId);
      setCurrentPROMIS33TaskId(task.taskId);
      setShowPROMIS33(true);
      return;
    }

    // Si c'est le questionnaire HOOS
    if (task.taskId.includes('hoos')) {
      console.log('HOOS détecté, taskId:', task.taskId);
      setCurrentHOOSTaskId(task.taskId);
      setShowHOOS(true);
      return;
    }

    // Si c'est le questionnaire DEGS
    if (task.taskId.includes('degs')) {
      console.log('DEGS détecté, taskId:', task.taskId);
      setCurrentDEGSTaskId(task.taskId);
      setShowDEGS(true);
      return;
    }

    // Si c'est le questionnaire Infection
    if (task.taskId.includes('infection')) {
      console.log('Infection détecté, taskId:', task.taskId);
      setCurrentInfectionTaskId(task.taskId);
      setShowInfection(true);
      return;
    }

    // Si c'est la mesure du tour de taille (tous les jours avec waist)
    if (task.taskId.includes('waist')) {
      setCurrentWaistTaskId(task.taskId);
      setShowWaistMeasurement(true);
      return;
    }

    // Si c'est la mesure du poids corporel (tous les jours avec weight)
    if (task.taskId.includes('weight')) {
      setCurrentWeightTaskId(task.taskId);
      setShowWeightMeasurement(true);
      return;
    }

    // Si c'est le test de corps cétoniques (tous les jours avec ketostix)
    if (task.taskId.includes('ketostix')) {
      setCurrentKetoneTaskId(task.taskId);
      setShowKetoneTest(true);
      return;
    }

    // Si c'est le questionnaire QoR-15 (jour 17 ou jour 24)
    if (task.taskId === 'day17-qor15' || task.taskId === 'day24-qor15') {
      setCurrentQoR15TaskId(task.taskId);
      setShowQoR15(true);
      return;
    }

    // Si c'est la mesure de tension artérielle (tous les jours avec blood-pressure)
    if (task.taskId.includes('blood-pressure')) {
      setCurrentBloodPressureTaskId(task.taskId);
      setShowBloodPressure(true);
      return;
    }
    
    // Pour les autres tâches, utiliser le comportement par défaut
    handleTaskToggle(task.taskId, task.completed);
  };

  const handleTaskToggle = async (taskId, currentStatus) => {
    if (!patientData || !patientData.patientId) return;

    // Mettre à jour l'état local immédiatement
    setLocalDay(prevDay => ({
      ...prevDay,
      tasks: prevDay.tasks.map(task => 
        task.taskId === taskId 
          ? { 
              ...task, 
              completed: !currentStatus,
              completedAt: !currentStatus ? new Date().toISOString() : null
            }
          : task
      )
    }));

    setLoading(true);
    try {
      if (currentStatus) {
        await apiService.markTaskIncomplete(patientData.patientId, taskId);
      } else {
        await apiService.markTaskCompleted(patientData.patientId, taskId);
      }

      // Recharger les données depuis le serveur pour s'assurer qu'elles sont à jour
      const response = await apiService.getPatientTimeline(patientData.patientId);
      if (response.success && response.timeline) {
        updateTimelineCache(response);
        const phase = response.timeline.find(p => p.phaseId === phaseId);
        if (phase) {
          const updatedDay = phase.days.find(d => d.dayNumber === day.dayNumber);
          if (updatedDay) {
            setLocalDay(updatedDay);
          }
        }
      }

      console.log('Tâche mise à jour avec succès');
    } catch (error) {
      console.error('Erreur mise à jour tâche:', error);
      // En cas d'erreur, remettre l'état précédent
      setLocalDay(prevDay => ({
        ...prevDay,
        tasks: prevDay.tasks.map(task => 
          task.taskId === taskId 
            ? { ...task, completed: currentStatus }
            : task
        )
      }));
    } finally {
      setLoading(false);
    }
  };



  const handlePROMIS33Complete = async (responses) => {
    // Utiliser le taskId dynamique pour PROMIS-33
    const promis33TaskId = currentPROMIS33TaskId;
    
    setShowPROMIS33(false);
    
    // Recharger les données depuis le serveur pour s'assurer qu'elles sont à jour
    try {
      const response = await apiService.getPatientTimeline(patientData.patientId);
      if (response.success && response.timeline) {
        updateTimelineCache(response);
        const phase = response.timeline.find(p => p.phaseId === phaseId);
        if (phase) {
          const updatedDay = phase.days.find(d => d.dayNumber === day.dayNumber);
          if (updatedDay) {
            // Nettoyer les doublons et mettre à jour avec les données du serveur
            const cleanedDay = {
              ...updatedDay,
              tasks: removeDuplicateTasks(updatedDay.tasks)
            };
            setLocalDay(cleanedDay);
          }
        }
      }
    } catch (error) {
      console.error('Erreur rechargement données après PROMIS-33:', error);
      // En cas d'erreur, faire une mise à jour locale minimale
      setLocalDay(prevDay => ({
        ...prevDay,
        tasks: prevDay.tasks.map(task => 
          task.taskId === promis33TaskId 
            ? { 
                ...task, 
                completed: true,
                completedAt: new Date().toISOString()
              }
            : task
        )
      }));
    }
    
    // Notifier le parent du changement
    if (onTaskUpdate) {
      onTaskUpdate();
    }
  };

  const handleHOOSComplete = async (responses) => {
    // Utiliser le taskId dynamique pour HOOS
    const hoosTaskId = currentHOOSTaskId;
    
    setShowHOOS(false);
    
    // Recharger les données depuis le serveur pour s'assurer qu'elles sont à jour
    try {
      const response = await apiService.getPatientTimeline(patientData.patientId);
      if (response.success && response.timeline) {
        updateTimelineCache(response);
        const phase = response.timeline.find(p => p.phaseId === phaseId);
        if (phase) {
          const updatedDay = phase.days.find(d => d.dayNumber === day.dayNumber);
          if (updatedDay) {
            // Nettoyer les doublons et mettre à jour avec les données du serveur
            const cleanedDay = {
              ...updatedDay,
              tasks: removeDuplicateTasks(updatedDay.tasks)
            };
            setLocalDay(cleanedDay);
          }
        }
      }
    } catch (error) {
      console.error('Erreur rechargement données après HOOS:', error);
      // En cas d'erreur, faire une mise à jour locale minimale
      setLocalDay(prevDay => ({
        ...prevDay,
        tasks: prevDay.tasks.map(task => 
          task.taskId === hoosTaskId 
            ? { 
                ...task, 
                completed: true,
                completedAt: new Date().toISOString()
              }
            : task
        )
      }));
    }
    
    // Notifier le parent du changement
    if (onTaskUpdate) {
      onTaskUpdate();
    }
  };

  const handleDEGSComplete = async (responses) => {
    // Utiliser le taskId dynamique pour DEGS
    const degsTaskId = currentDEGSTaskId;
    
    setShowDEGS(false);
    
    // Recharger les données depuis le serveur pour s'assurer qu'elles sont à jour
    try {
      const response = await apiService.getPatientTimeline(patientData.patientId);
      if (response.success && response.timeline) {
        updateTimelineCache(response);
        const phase = response.timeline.find(p => p.phaseId === phaseId);
        if (phase) {
          const updatedDay = phase.days.find(d => d.dayNumber === day.dayNumber);
          if (updatedDay) {
            // Nettoyer les doublons et mettre à jour avec les données du serveur
            const cleanedDay = {
              ...updatedDay,
              tasks: removeDuplicateTasks(updatedDay.tasks)
            };
            setLocalDay(cleanedDay);
          }
        }
      }
    } catch (error) {
      console.error('Erreur rechargement données après DEGS:', error);
      // En cas d'erreur, faire une mise à jour locale minimale
      setLocalDay(prevDay => ({
        ...prevDay,
        tasks: prevDay.tasks.map(task => 
          task.taskId === degsTaskId 
            ? { 
                ...task, 
                completed: true,
                completedAt: new Date().toISOString()
              }
            : task
        )
      }));
    }
    
    // Notifier le parent du changement
    if (onTaskUpdate) {
      onTaskUpdate();
    }
  };

  const handleInfectionComplete = async (responses) => {
    // Utiliser le taskId dynamique pour Infection
    const infectionTaskId = currentInfectionTaskId;
    
    setShowInfection(false);
    
    // Recharger les données depuis le serveur pour s'assurer qu'elles sont à jour
    try {
      const response = await apiService.getPatientTimeline(patientData.patientId);
      if (response.success && response.timeline) {
        updateTimelineCache(response);
        const phase = response.timeline.find(p => p.phaseId === phaseId);
        if (phase) {
          const updatedDay = phase.days.find(d => d.dayNumber === day.dayNumber);
          if (updatedDay) {
            // Nettoyer les doublons et mettre à jour avec les données du serveur
            const cleanedDay = {
              ...updatedDay,
              tasks: removeDuplicateTasks(updatedDay.tasks)
            };
            setLocalDay(cleanedDay);
          }
        }
      }
    } catch (error) {
      console.error('Erreur rechargement données après Infection:', error);
      // En cas d'erreur, faire une mise à jour locale minimale
      setLocalDay(prevDay => ({
        ...prevDay,
        tasks: prevDay.tasks.map(task => 
          task.taskId === infectionTaskId 
            ? { 
                ...task, 
                completed: true,
                completedAt: new Date().toISOString()
              }
            : task
        )
      }));
    }
    
    // Notifier le parent du changement
    if (onTaskUpdate) {
      onTaskUpdate();
    }
  };

  const handleWaistMeasurementComplete = async (measurement, taskId) => {
    // Mettre à jour la tâche comme complétée
    setLocalDay(prevDay => ({
      ...prevDay,
      tasks: prevDay.tasks.map(task => 
        task.taskId === taskId 
          ? { 
              ...task, 
              completed: true,
              completedAt: new Date().toISOString()
            }
          : task
      )
    }));
    
    setShowWaistMeasurement(false);
    setWaistMeasurement(measurement); // Sauvegarder la mesure pour l'affichage
    
    // Recharger les données depuis le serveur pour s'assurer qu'elles sont à jour
    try {
      const response = await apiService.getPatientTimeline(patientData.patientId);
      if (response.success && response.timeline) {
        updateTimelineCache(response);
        const phase = response.timeline.find(p => p.phaseId === phaseId);
        if (phase) {
          const updatedDay = phase.days.find(d => d.dayNumber === day.dayNumber);
          if (updatedDay) {
            setLocalDay(updatedDay);
          }
        }
      }
    } catch (error) {
      console.error('Erreur rechargement données après mesure tour de taille:', error);
    }
    
    // Notifier le parent du changement
    if (onTaskUpdate) {
      onTaskUpdate();
    }
  };

  const handleWeightMeasurementComplete = async (measurement, taskId) => {
    // Mettre à jour la tâche comme complétée
    setLocalDay(prevDay => ({
      ...prevDay,
      tasks: prevDay.tasks.map(task => 
        task.taskId === taskId 
          ? { 
              ...task, 
              completed: true,
              completedAt: new Date().toISOString()
            }
          : task
      )
    }));
    
    setShowWeightMeasurement(false);
    setWeightMeasurement(measurement); // Sauvegarder la mesure pour l'affichage
    
    // Recharger les données depuis le serveur pour s'assurer qu'elles sont à jour
    try {
      const response = await apiService.getPatientTimeline(patientData.patientId);
      if (response.success && response.timeline) {
        updateTimelineCache(response);
        const phase = response.timeline.find(p => p.phaseId === phaseId);
        if (phase) {
          const updatedDay = phase.days.find(d => d.dayNumber === day.dayNumber);
          if (updatedDay) {
            setLocalDay(updatedDay);
          }
        }
      }
    } catch (error) {
      console.error('Erreur rechargement données après mesure poids:', error);
    }
    
    // Notifier le parent du changement
    if (onTaskUpdate) {
      onTaskUpdate();
    }
  };

  const handleKetoneTestComplete = async (ketoneValue, taskId = 'day1-ketostix') => {
    // Mettre à jour la tâche comme complétée
    setLocalDay(prevDay => ({
      ...prevDay,
      tasks: prevDay.tasks.map(task => 
        task.taskId === taskId 
          ? { 
              ...task, 
              completed: true,
              completedAt: new Date().toISOString()
            }
          : task
      )
    }));
    
    setShowKetoneTest(false);
    
    // Recharger les données depuis le serveur pour s'assurer qu'elles sont à jour
    try {
      const response = await apiService.getPatientTimeline(patientData.patientId);
      if (response.success && response.timeline) {
        updateTimelineCache(response);
        const phase = response.timeline.find(p => p.phaseId === phaseId);
        if (phase) {
          const updatedDay = phase.days.find(d => d.dayNumber === day.dayNumber);
          if (updatedDay) {
            setLocalDay(updatedDay);
          }
        }
      }
    } catch (error) {
      console.error('Erreur rechargement données après test cétonique:', error);
    }
    
    // Notifier le parent du changement
    if (onTaskUpdate) {
      onTaskUpdate();
    }
  };

  const handleQoR15Complete = async (score, responses) => {
    // Mettre à jour la tâche comme complétée
    setLocalDay(prevDay => ({
      ...prevDay,
      tasks: prevDay.tasks.map(task => 
        task.taskId === currentQoR15TaskId 
          ? { 
              ...task, 
              completed: true,
              completedAt: new Date().toISOString()
            }
          : task
      )
    }));
    
    setShowQoR15(false);
    
    // Recharger les données depuis le serveur pour s'assurer qu'elles sont à jour
    try {
      const response = await apiService.getPatientTimeline(patientData.patientId);
      if (response.success && response.timeline) {
        updateTimelineCache(response);
        const phase = response.timeline.find(p => p.phaseId === phaseId);
        if (phase) {
          const updatedDay = phase.days.find(d => d.dayNumber === day.dayNumber);
          if (updatedDay) {
            setLocalDay(updatedDay);
          }
        }
      }
    } catch (error) {
      console.error('Erreur rechargement données après QoR-15:', error);
    }
    
    // Notifier le parent du changement
    if (onTaskUpdate) {
      onTaskUpdate();
    }
  };

  const handleBloodPressureComplete = async (measurement, taskId) => {
    // Mettre à jour la tâche comme complétée
    setLocalDay(prevDay => ({
      ...prevDay,
      tasks: prevDay.tasks.map(task => 
        task.taskId === taskId 
          ? { 
              ...task, 
              completed: true,
              completedAt: new Date().toISOString()
            }
          : task
      )
    }));
    
    setShowBloodPressure(false);
    
    // Recharger les données depuis le serveur pour s'assurer qu'elles sont à jour
    try {
      const response = await apiService.getPatientTimeline(patientData.patientId);
      if (response.success && response.timeline) {
        updateTimelineCache(response);
        const phase = response.timeline.find(p => p.phaseId === phaseId);
        if (phase) {
          const updatedDay = phase.days.find(d => d.dayNumber === day.dayNumber);
          if (updatedDay) {
            setLocalDay(updatedDay);
          }
        }
      }
    } catch (error) {
      console.error('Erreur rechargement données après tension artérielle:', error);
    }
    
    // Notifier le parent du changement
    if (onTaskUpdate) {
      onTaskUpdate();
    }
  };

  // Charger les scores OHS si les tâches sont complétées
  useEffect(() => {
    const loadOHSScores = async () => {
      // Charger le score OHS du jour 1
      const ohsTask1 = localDay.tasks.find(task => task.taskId === 'day1-ohs');
      if (ohsTask1 && ohsTask1.completed && !ohsScore) {
        try {
          const response = await apiService.getQuestionnaireResponse(patientData.patientId, 'day1-ohs');
          if (response.success && response.data) {
            setOhsScore(response.data.score);
          }
        } catch (error) {
          console.error('Erreur chargement score OHS jour 1:', error);
        }
      }
      
      // Charger le score OHS du jour 24
      const ohsTask24 = localDay.tasks.find(task => task.taskId === 'day24-ohs');
      if (ohsTask24 && ohsTask24.completed && !ohsScoreDay24) {
        try {
          const response = await apiService.getQuestionnaireResponse(patientData.patientId, 'day24-ohs');
          if (response.success && response.data) {
            setOhsScoreDay24(response.data.score);
          }
        } catch (error) {
          console.error('Erreur chargement score OHS jour 24:', error);
        }
      }
    };
    
    loadOHSScores();
  }, [localDay.tasks, patientData.patientId, ohsScore, ohsScoreDay24]);

  // Charger les scores SF-36 si les tâches sont complétées
  useEffect(() => {
    const loadSF36Scores = async () => {
      // Charger les scores SF-36 du jour 1
      const sf36Task1 = localDay.tasks.find(task => task.taskId === 'day1-sf36');
      if (sf36Task1 && sf36Task1.completed && !sf36Scores) {
        try {
          const response = await apiService.getQuestionnaireResponse(patientData.patientId, 'day1-sf36');
          if (response.success && response.data) {
            const scores = JSON.parse(response.data.score);
            const responses = JSON.parse(response.data.responses);
            setSf36Scores(scores);
            setSf36Responses(responses);
          }
        } catch (error) {
          console.error('Erreur chargement scores SF-36 jour 1:', error);
        }
      }
      
      // Charger les scores SF-36 du jour 24
      const sf36Task24 = localDay.tasks.find(task => task.taskId === 'day24-sf36');
      if (sf36Task24 && sf36Task24.completed && !sf36ScoresDay24) {
        try {
          const response = await apiService.getQuestionnaireResponse(patientData.patientId, 'day24-sf36');
          if (response.success && response.data) {
            const scores = JSON.parse(response.data.score);
            const responses = JSON.parse(response.data.responses);
            setSf36ScoresDay24(scores);
            setSf36ResponsesDay24(responses);
          }
        } catch (error) {
          console.error('Erreur chargement scores SF-36 jour 24:', error);
        }
      }
    };
    
    loadSF36Scores();
  }, [localDay.tasks, patientData.patientId, sf36Scores, sf36ScoresDay24]);

  // Charger la mesure du tour de taille si la tâche est complétée
  useEffect(() => {
    const waistTask = localDay.tasks.find(task => task.taskId === 'day1-waist');
    if (waistTask && waistTask.completed && !waistMeasurement) {
      // Récupérer la mesure depuis l'API
      const loadWaistMeasurement = async () => {
        try {
          const response = await apiService.getQuestionnaireResponse(patientData.patientId, 'day1-waist');
          if (response.success && response.data) {
            setWaistMeasurement(response.data.score);
          }
        } catch (error) {
          console.error('Erreur chargement mesure tour de taille:', error);
        }
      };
      loadWaistMeasurement();
    }
  }, [localDay.tasks, patientData.patientId, waistMeasurement]);

  // Charger la mesure du poids si la tâche est complétée
  useEffect(() => {
    const weightTask = localDay.tasks.find(task => task.taskId === 'day1-weight');
    if (weightTask && weightTask.completed && !weightMeasurement) {
      // Récupérer la mesure depuis l'API
      const loadWeightMeasurement = async () => {
        try {
          const response = await apiService.getQuestionnaireResponse(patientData.patientId, 'day1-weight');
          if (response.success && response.data) {
            setWeightMeasurement(response.data.score);
          }
        } catch (error) {
          console.error('Erreur chargement mesure poids:', error);
        }
      };
      loadWeightMeasurement();
    }
  }, [localDay.tasks, patientData.patientId, weightMeasurement]);



  const completedTasks = localDay.tasks.filter(task => task.completed).length;
  const totalTasks = localDay.tasks.length;
  
  // Calcul spécial pour la phase Surgery
  let progressPercentage;
  if (phaseId === 'surgery') {
    // Calculer les jours depuis le début
    const today = new Date();
    const startDate = patientData ? new Date(patientData.startDate + 'T00:00:00.000Z') : null;
    const daysSinceStart = startDate ? Math.max(0, Math.floor((today - startDate) / (1000 * 60 * 60 * 24))) : 0;
    
    // Si plus de 12 jours, 100% sinon 0%
    progressPercentage = daysSinceStart > 12 ? 100 : 0;
  } else {
    progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
  }

  return (
    <div className="day-detail-overlay">
      <div className="day-detail-modal">
        <div className="day-detail-header">
          <h2 className="day-detail-title">
            {t.timeline?.dayTranslations?.[phaseId]?.[localDay.dayNumber.toString()]?.title || `Day ${localDay.dayNumber}`}
          </h2>
          <button className="day-detail-close" onClick={onClose}>×</button>
        </div>

        <div className="day-detail-description">
          <p>
            {t.timeline?.dayTranslations?.[phaseId]?.[localDay.dayNumber.toString()]?.description || ''}
          </p>
        </div>

        <div className="day-progress-summary">
          <div className="progress-circle">
            <svg width="60" height="60" viewBox="0 0 60 60">
              <circle
                cx="30"
                cy="30"
                r="25"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="4"
              />
              <circle
                cx="30"
                cy="30"
                r="25"
                fill="none"
                stroke="#10b981"
                strokeWidth="4"
                strokeDasharray={`${2 * Math.PI * 25}`}
                strokeDashoffset={`${2 * Math.PI * 25 * (1 - progressPercentage / 100)}`}
                transform="rotate(-90 30 30)"
              />
            </svg>
            <div className="progress-text">
              <span className="progress-number">{phaseId === 'surgery' ? 0 : completedTasks}</span>
              <span className="progress-total">/{phaseId === 'surgery' ? 0 : totalTasks}</span>
            </div>
          </div>
          <div className="progress-info">
            <p className="progress-percentage">{Math.round(progressPercentage)}%</p>
            <p className="progress-label">{t.dayDetail?.completed || t.timeline?.completed || 'Completed'}</p>
          </div>
        </div>

        {/* Vidéo pour les jours 1-5 de la phase Buchinger */}
        {videoData && (
          <div className="day-video-section">
            <h3 className="video-title">{language === 'de' ? 'Video' : 'Video'}</h3>
            <YouTubeFrame
              videoUrl={videoData.url}
              title={videoData.title}
              language={language}
              showTitle={false}
            />
          </div>
        )}

        {/* Article 14/10 Intervallfasten pour les jours -15 et -14 */}
        {localDay && (localDay.dayNumber === -15 || localDay.dayNumber === -14) && (
          <div className="day-video-section">
            <h3 className="video-title">Artikel</h3>
            <div className="interval-fasting-article">
              <div className="article-content">
                <div className="article-header">
                  <img src="/images/article-icon.png" alt="Article" className="article-icon" />
                  <h4>14/10 Intervallfasten - Leitfaden</h4>
                </div>
                <p>Erfahren Sie alles über das 14/10 Intervallfasten: Grundprinzipien, Vorteile und praktische Tipps für eine erfolgreiche Umsetzung dieser Fastenmethode.</p>
                <button 
                  className="article-read-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/article');
                  }}
                >
                  Artikel lesen
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="tasks-list">
          <h3 className="tasks-title">{t.dayDetail?.tasks || t.timeline?.tasks || 'Tasks'}</h3>
          {phaseId === 'surgery' ? (
            <div className="surgery-day-content">
              <div className="no-tasks-message">
                <p>{language === 'de' ? 'Keine Aufgaben - nur einige Tipps' : 'No tasks - just some tips'}</p>
              </div>
              <SurgeryTips language={language} />
            </div>
          ) : (
            removeDuplicateTasks(localDay.tasks).map((task) => (
            <React.Fragment key={task.taskId}>
              <div 
                className={`task-item ${task.completed ? 'completed' : ''}`}
              >
                <div className="task-header">
                  <div className="task-icon" style={{ color: getTaskTypeColor(task.taskType) }}>
                    {getTaskIcon(task.taskType, task.taskId).startsWith('/') ? (
                      <img src={getTaskIcon(task.taskType, task.taskId)} alt="Task icon" style={{ width: '20px', height: '20px' }} />
                    ) : (
                      getTaskIcon(task.taskType, task.taskId)
                    )}
                  </div>
                  <div className="task-content">
                    <h4 className="task-title">
                      {t.tasks?.[task.taskId]?.title || task.title}
                    </h4>
                    <p className="task-description">
                      {t.tasks?.[task.taskId]?.description || task.description}
                    </p>
                  </div>
                  <div className="task-actions">
                    {(task.taskType === 'questionnaire' || task.taskType === 'measurement' || task.taskType === 'test') && task.completed && (
                      <button
                        className="task-edit-btn"
                        onClick={() => handleTaskClick(task)}
                        disabled={loading}
                        title={language === 'de' ? 'Bearbeiten' : 'Edit'}
                      >
                        <img src="/images/edit.png" alt="Edit" className="task-edit-icon" />
                      </button>
                    )}
                    <button
                      className={`task-toggle-btn ${task.completed ? 'completed' : ''} ${loading ? 'loading' : ''}`}
                      onClick={() => handleTaskClick(task)}
                      disabled={loading}
                    >
                      {task.completed ? (
                        <img src="/images/checked.png" alt="Completed" className="task-status-icon" />
                      ) : (
                        '○'
                      )}
                    </button>
                  </div>
                </div>
                {task.completed && task.completedAt && (
                  <div className="task-completion-info">
                    <span className="completion-date">
                      {t.dayDetail?.completedOn || t.timeline?.completedOn || 'Completed on'} {new Date(task.completedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>


              {/* Afficher la mesure du tour de taille */}
              {(task.taskId === 'day1-waist' || task.taskId === 'day3-waist' || task.taskId === 'day5-waist' || task.taskId === 'day7-waist' || task.taskId === 'day9-waist' || task.taskId === 'day11-waist' || task.taskId === 'day13-waist' || task.taskId === 'day15-waist' || task.taskId === 'day17-waist' || task.taskId === 'day19-waist' || task.taskId === 'day21-waist' || task.taskId === 'day23-waist') && task.completed && waistMeasurement !== null && (
                <div className="waist-measurement-display">
                  <div className="measurement-result">
                    <span className="measurement-label">
                      {language === 'de' ? 'Taillenumfang:' : 'Waist circumference:'}
                    </span>
                    <span className="measurement-value">
                      {waistMeasurement} cm
                    </span>
                  </div>
                </div>
              )}
              {/* Afficher la mesure du poids */}
              {(task.taskId === 'day1-weight' || task.taskId === 'day3-weight' || task.taskId === 'day5-weight' || task.taskId === 'day7-weight' || task.taskId === 'day9-weight' || task.taskId === 'day11-weight' || task.taskId === 'day13-weight' || task.taskId === 'day15-weight' || task.taskId === 'day17-weight' || task.taskId === 'day19-weight' || task.taskId === 'day21-weight' || task.taskId === 'day23-weight') && task.completed && weightMeasurement !== null && (
                <div className="waist-measurement-display">
                  <div className="measurement-result">
                    <span className="measurement-label">
                      {language === 'de' ? 'Körpergewicht:' : 'Body weight:'}
                    </span>
                    <span className="measurement-value">
                      {weightMeasurement} kg
                    </span>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))
          )}
        </div>
      </div>





      {/* Modal PROMIS-33 */}
      {showPROMIS33 && (
        <div>
          {console.log('Modal PROMIS-33 affiché, taskId:', currentPROMIS33TaskId)}
          <PROMIS33Questionnaire
            patientData={patientData}
            taskId={currentPROMIS33TaskId}
            language={language}
            onComplete={handlePROMIS33Complete}
            onClose={() => setShowPROMIS33(false)}
          />
        </div>
      )}

      {/* Modal HOOS */}
      {showHOOS && (
        <div>
          {console.log('Modal HOOS affiché, taskId:', currentHOOSTaskId)}
          <HOOSQuestionnaire
            patientData={patientData}
            taskId={currentHOOSTaskId}
            language={language}
            onComplete={handleHOOSComplete}
            onClose={() => setShowHOOS(false)}
          />
        </div>
      )}

      {/* Modal DEGS */}
      {showDEGS && (
        <div>
          {console.log('Modal DEGS affiché, taskId:', currentDEGSTaskId, 'patientId:', patientData?.patientId)}
          <DEGSQuestionnaire
            patientId={patientData?.patientId}
            taskId={currentDEGSTaskId}
            language={language}
            onComplete={handleDEGSComplete}
            onClose={() => setShowDEGS(false)}
          />
        </div>
      )}

      {/* Modal Infection */}
      {showInfection && (
        <div>
          {console.log('Modal Infection affiché, taskId:', currentInfectionTaskId)}
          <InfectionQuestionnaire
            patientData={patientData}
            taskId={currentInfectionTaskId}
            language={language}
            onComplete={handleInfectionComplete}
            onClose={() => setShowInfection(false)}
          />
        </div>
      )}

      {/* Modal Mesure Tour de Taille */}
      {showWaistMeasurement && (
        <WaistMeasurement
          patientData={patientData}
          taskId={currentWaistTaskId}
          language={language}
          onComplete={(measurement) => handleWaistMeasurementComplete(measurement, currentWaistTaskId)}
          onClose={() => setShowWaistMeasurement(false)}
        />
      )}

      {/* Modal Mesure Poids */}
      {showWeightMeasurement && (
        <WeightMeasurement
          patientData={patientData}
          taskId={currentWeightTaskId}
          language={language}
          onComplete={(measurement) => handleWeightMeasurementComplete(measurement, currentWeightTaskId)}
          onClose={() => setShowWeightMeasurement(false)}
        />
      )}

      {/* Modal Test Cétoniques */}
      {showKetoneTest && (
        <KetoneTest
          patientData={patientData}
          taskId={currentKetoneTaskId}
          language={language}
          onComplete={(ketoneValue) => handleKetoneTestComplete(ketoneValue, currentKetoneTaskId)}
          onClose={() => setShowKetoneTest(false)}
        />
      )}

      {/* Modal QoR-15 */}
      {showQoR15 && (
        <QoR15Questionnaire
          patientData={patientData}
          taskId={currentQoR15TaskId}
          language={language}
          onComplete={handleQoR15Complete}
          onClose={() => setShowQoR15(false)}
        />
      )}

      {/* Modal Mesure Tension Artérielle */}
      {showBloodPressure && (
        <BloodPressureMeasurement
          patientData={patientData}
          taskId={currentBloodPressureTaskId}
          language={language}
          onComplete={(measurement) => handleBloodPressureComplete(measurement, currentBloodPressureTaskId)}
          onClose={() => setShowBloodPressure(false)}
        />
      )}
    </div>
  );
}

export default DayDetail; 